<template>
    <div class="view min-h-screen overflow-hidden bg-view-sand-light text-dark-blue">
        <Header />

        <main class="relative flex min-h-[100svh] items-center py-36">
            <section class="container relative z-10" aria-labelledby="error-title">
                <div class="col-span-full md:col-start-2 md:col-span-10 lg:col-start-3 lg:col-span-8">
                    <h1 id="error-title" class="view-h1 max-w-4xl">
                        {{ t('title') }}
                    </h1>
                    <p class="mt-7 max-w-2xl text-dark-blue/75">
                        {{ t('description') }}
                    </p>

                    <div class="mt-10 flex flex-wrap gap-3">
                        <button
                            type="button"
                            class="rounded-full bg-dark-blue px-6 py-3 text-white transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-dark-blue"
                            @click="retry"
                        >
                            {{ t('try-again') }}
                        </button>
                        <button
                            type="button"
                            class="rounded-full border border-dark-blue px-6 py-3 transition-colors hover:bg-dark-blue hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-dark-blue"
                            @click="goHome"
                        >
                            {{ t('go-home') }}
                        </button>
                    </div>

                    <p class="mt-8 text-caption text-dark-blue/50">
                        {{ t('logged') }}
                    </p>
                </div>
            </section>
        </main>

        <Footer />
    </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'
import Header from '~/components/view/Header.vue'
import Footer from '~/components/view/Footer.vue'
import HeaderLogo from "~/components/view/HeaderLogo.vue"

const props = defineProps<{
    error: NuxtError
}>()

const route = useRoute()
const localePath = useLocalePath()
const siteStore = useSiteStore()
const { t } = useI18n({ useScope: 'local' })
const reportedOnServer = useState('public-error-reported-on-server', () => import.meta.server)
const originalPath = computed(() => {
    return typeof props.error.url === 'string' && props.error.url.startsWith('/')
        ? props.error.url
        : route.fullPath
})

siteStore.setIconColor('dark')

useHead({
    title: () => t('meta-title'),
    htmlAttrs: {
        class: 'view-layout light'
    }
})

onMounted(() => {
    if (reportedOnServer.value) return

    reportedOnServer.value = true
    $fetch('/api/errors/report', {
        method: 'POST',
        body: {
            path: originalPath.value,
            statusCode: props.error.statusCode,
            message: props.error.message,
            stack: props.error.stack
        }
    }).catch(() => {
        // Reporting must never replace the safe public error page with another error.
    })
})

const clearReportingState = () => {
    reportedOnServer.value = false
}

const retry = () => {
    clearReportingState()
    return clearError({ redirect: originalPath.value })
}

const goHome = () => {
    clearReportingState()
    return clearError({ redirect: localePath('/') })
}
</script>

<style scoped>
</style>

<i18n lang="yaml">
en:
    meta-title: Something went wrong
    eyebrow: Unexpected detour
    title: Something went wrong
    description: We couldn't open this page right now. You can try again, or head back home and continue exploring.
    try-again: Try again
    go-home: Go to homepage
    logged: The issue has been logged so we can look into it.
nl:
    meta-title: Er ging iets mis
    eyebrow: Onverwachte omweg
    title: Er ging iets mis
    description: We kunnen deze pagina nu niet openen. Probeer het opnieuw of ga terug naar de homepage.
    try-again: Probeer opnieuw
    go-home: Ga naar de homepage
    logged: Het probleem is gelogd, zodat we het kunnen onderzoeken.
</i18n>
